// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 MindTenet LLC
// This file is part of Undef Engine.

import { create } from 'zustand';
import { useConnectivityStore } from './connectivityStore';
import { useGameSessionStore } from './gameSessionStore';
import { useGameStateStore } from './gameStateStore';
import { usePlayerStore } from './playerStore';
import { useRoomSpectatorStore } from './roomSpectatorStore';
import { useTimerStore } from './timerStore';
import type { ConnectivityStatus } from './latencyHelpers';
import type {
  GameState,
  LobbyState,
  PlayerInfo,
  SpectatorInfo,
  LobbySettings,
} from '../types/game';

interface GameStore {
  // Session info
  sessionId: string | null;
  roomCode: string | null;
  playerId: string | null;
  playerToken: string | null;
  displayName: string | null;
  sessionRestorePending: boolean;
  isPublic: boolean;

  // WebSocket connection state
  isConnected: boolean;

  // Game state
  gameState: GameState | LobbyState | null;

  // Delta sync tracking
  lastSeenSequence: number;

  // Spectators and settings
  spectators: SpectatorInfo[];
  lobbySettings: LobbySettings | null;
  allowSpectators: boolean;

  // Error state
  sessionError: string | null;
  showSessionErrorModal: boolean;

  // Connection latency metrics
  currentLatency: number;
  latencyHistory: number[];
  latencyP95: number;
  connectivityStatus: ConnectivityStatus;

  // Actions
  setSession: (
    sessionId: string,
    roomCode: string,
    playerId: string,
    playerToken: string,
    displayName: string
  ) => void;
  setSessionId: (sessionId: string) => void;
  setRoomCode: (roomCode: string) => void;
  setPlayerId: (playerId: string) => void;
  setPlayerToken: (playerToken: string) => void;
  setDisplayName: (displayName: string) => void;
  setSessionRestorePending: (pending: boolean) => void;
  updateGameState: (state: GameState | LobbyState, sequence?: number) => void;
  updateSequence: (sequence: number) => void;
  getLastSeenSequence: () => number;
  clearSession: () => void;
  setSessionError: (error: string) => void;
  clearSessionError: () => void;
  setRoomPrivacy: (isPublic: boolean) => void;
  toggleRoomPrivacy: () => void;
  setConnected: (connected: boolean) => void;

  // Connection latency actions
  updateLatency: (rtt: number) => void;
  getAverageLatency: () => number;
  getLatencyPercentile: (percentile: number) => number;

  // Player actions
  addPlayer: (player: PlayerInfo) => void;
  removePlayer: (playerId: string) => void;
  updatePlayerNpcStatus: (
    playerId: string,
    updates: { is_npc: boolean; display_name?: string; claim_code?: string | null }
  ) => void;
  updatePlayerProfile: (
    playerId: string,
    profile: {
      profile_emoji?: string | null;
      theme_primary_color?: string | null;
    }
  ) => void;
  reorderPlayersForHost: (newHostId: string, isCurrentPlayer: boolean) => void;

  // Spectator actions
  addSpectator: (spectator: SpectatorInfo) => void;
  removeSpectator: (playerId: string) => void;
  setSpectators: (spectators: SpectatorInfo[]) => void;

  // Lobby settings actions
  updateLobbySettings: (
    settings: LobbySettings,
    changes?: Record<string, { old: string | number | boolean; new: string | number | boolean }>
  ) => void;
  setAllowSpectators: (allow: boolean) => void;

  // Selectors
  isInLobby: () => boolean;
  isInGame: () => boolean;
  getCurrentPlayer: () => PlayerInfo | null;
}

export const useGameStore = create<GameStore>()((_set, _get) => ({
  // Define initial state values based on the sub-stores
  ...useGameSessionStore.getState(),
  ...useGameStateStore.getState(),
  ...useRoomSpectatorStore.getState(),
  ...useConnectivityStore.getState(),

  // Game session actions — resolved from current sub-store state at call time
  // to avoid stale function references captured at module load
  setSession: (
    ...args: Parameters<ReturnType<typeof useGameSessionStore.getState>['setSession']>
  ) => useGameSessionStore.getState().setSession(...args),
  setSessionId: (sessionId: string) => useGameSessionStore.getState().setSessionId(sessionId),
  setRoomCode: (roomCode: string) => useGameSessionStore.getState().setRoomCode(roomCode),
  setPlayerId: (playerId: string) => useGameSessionStore.getState().setPlayerId(playerId),
  setPlayerToken: (playerToken: string) =>
    useGameSessionStore.getState().setPlayerToken(playerToken),
  setDisplayName: (displayName: string) =>
    useGameSessionStore.getState().setDisplayName(displayName),
  setSessionRestorePending: (pending: boolean) =>
    useGameSessionStore.getState().setSessionRestorePending(pending),
  clearSession: () => {
    useGameSessionStore.getState().clearSession();
    useGameStateStore.getState().clearGameState();
    // Sync facade immediately with explicit data fields only
    // (spreading full sub-store state would overwrite facade functions)
    useGameStore.setState({
      sessionId: null,
      roomCode: null,
      playerId: null,
      playerToken: null,
      displayName: null,
      sessionRestorePending: false,
      isPublic: true,
      sessionError: null,
      showSessionErrorModal: false,
      gameState: null,
      lastSeenSequence: 0,
      lobbySettings: null,
    });
  },
  setSessionError: (error: string) => useGameSessionStore.getState().setSessionError(error),
  clearSessionError: () => useGameSessionStore.getState().clearSessionError(),
  setRoomPrivacy: (isPublic: boolean) => useGameSessionStore.getState().setRoomPrivacy(isPublic),
  toggleRoomPrivacy: () => useGameSessionStore.getState().toggleRoomPrivacy(),

  // Game state actions
  updateGameState: (state, sequence) => {
    useGameStateStore
      .getState()
      .updateGameState(state, useGameSessionStore.getState().playerId, sequence);
    // Sync facade immediately
    useGameStore.setState({
      gameState: useGameStateStore.getState().gameState,
    } as unknown as Partial<GameStore>);
  },
  updateSequence: (sequence: number) => useGameStateStore.getState().updateSequence(sequence),
  getLastSeenSequence: () => useGameStateStore.getState().getLastSeenSequence(),

  // Connectivity
  setConnected: (connected: boolean) => useConnectivityStore.getState().setConnected(connected),
  updateLatency: (rtt: number) => useConnectivityStore.getState().updateLatency(rtt),
  getAverageLatency: () => useConnectivityStore.getState().getAverageLatency(),
  getLatencyPercentile: (percentile: number) =>
    useConnectivityStore.getState().getLatencyPercentile(percentile),

  // Player actions
  addPlayer: (...args: Parameters<ReturnType<typeof usePlayerStore.getState>['addPlayer']>) =>
    usePlayerStore.getState().addPlayer(...args),
  removePlayer: (playerId: string) => usePlayerStore.getState().removePlayer(playerId),
  updatePlayerNpcStatus: (
    ...args: Parameters<ReturnType<typeof usePlayerStore.getState>['updatePlayerNpcStatus']>
  ) => usePlayerStore.getState().updatePlayerNpcStatus(...args),
  updatePlayerProfile: (
    ...args: Parameters<ReturnType<typeof usePlayerStore.getState>['updatePlayerProfile']>
  ) => usePlayerStore.getState().updatePlayerProfile(...args),
  reorderPlayersForHost: (newHostId: string, isCurrentPlayer: boolean) =>
    usePlayerStore.getState().reorderPlayersForHost(newHostId, isCurrentPlayer),
  getCurrentPlayer: () => usePlayerStore.getState().getCurrentPlayer(),

  // Spectator and lobby settings
  addSpectator: (
    ...args: Parameters<ReturnType<typeof useRoomSpectatorStore.getState>['addSpectator']>
  ) => useRoomSpectatorStore.getState().addSpectator(...args),
  removeSpectator: (playerId: string) => useRoomSpectatorStore.getState().removeSpectator(playerId),
  setSpectators: (spectators: SpectatorInfo[]) =>
    useRoomSpectatorStore.getState().setSpectators(spectators),
  updateLobbySettings: (
    ...args: Parameters<ReturnType<typeof useGameStateStore.getState>['updateLobbySettings']>
  ) => useGameStateStore.getState().updateLobbySettings(...args),
  setAllowSpectators: (allow: boolean) =>
    useRoomSpectatorStore.getState().setAllowSpectators(allow),

  // Helpers
  isInLobby: () => useGameStateStore.getState().isInLobby(),
  isInGame: () => useGameStateStore.getState().isInGame(),
}));

// Setup subscriptions to keep facade updated (sync only data fields, not functions)
useGameSessionStore.subscribe(state =>
  useGameStore.setState({
    sessionId: state.sessionId,
    roomCode: state.roomCode,
    playerId: state.playerId,
    playerToken: state.playerToken,
    displayName: state.displayName,
    sessionRestorePending: state.sessionRestorePending,
    isPublic: state.isPublic,
    sessionError: state.sessionError,
    showSessionErrorModal: state.showSessionErrorModal,
  })
);
useGameStateStore.subscribe(state =>
  useGameStore.setState({
    gameState: state.gameState,
    lastSeenSequence: state.lastSeenSequence,
    lobbySettings: state.lobbySettings,
  })
);
useConnectivityStore.subscribe(state =>
  useGameStore.setState({
    isConnected: state.isConnected,
    currentLatency: state.currentLatency,
    latencyHistory: state.latencyHistory,
    latencyP95: state.latencyP95,
    connectivityStatus: state.connectivityStatus,
  })
);
useRoomSpectatorStore.subscribe(state =>
  useGameStore.setState({
    spectators: state.spectators,
    allowSpectators: state.allowSpectators,
  })
);

// Expose stores on window for E2E testing (dev only — never in production builds)
if (import.meta.env.DEV && typeof window !== 'undefined') {
  const globalWindow = window as unknown as {
    useGameStore?: typeof useGameStore;
    useTimerStore?: typeof useTimerStore;
    __ZUSTAND_STORE__?: typeof useGameStore;
    __LATENCY_METRICS__?: {
      current: number;
      history: number[];
      average: number;
      p95: number;
      status: string;
    };
  };
  globalWindow.useGameStore = useGameStore;
  globalWindow.useTimerStore = useTimerStore;
  globalWindow.__ZUSTAND_STORE__ = useGameStore;

  Object.defineProperty(globalWindow, '__LATENCY_METRICS__', {
    get() {
      const state = useConnectivityStore.getState();
      return {
        current: state.currentLatency,
        history: state.latencyHistory,
        average: state.getAverageLatency(),
        p95: state.getLatencyPercentile(95),
        status: state.connectivityStatus,
      };
    },
    configurable: true,
  });
}

// Re-export sub-stores for direct access by new consumers
export { useConnectivityStore } from './connectivityStore';
export { useRoomSpectatorStore } from './roomSpectatorStore';
export { useGameSessionStore } from './gameSessionStore';
export { useGameStateStore } from './gameStateStore';
export { usePlayerStore } from './playerStore';
export { useTimerStore } from './timerStore';
